/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import Vue from 'vue';
import Vuex from 'vuex';

import auth from '@/modules/Flexigo/services/auth';
import { objectifyByKey } from '@/helpers/objectUtils';

import globalService from '../services/global';
import workgroupService from '../services/workgroup';
import companies from '../services/companies';
import settings from './modules/settings';
import settingsTabs from './modules/settingsTabs';
import workgroups from './modules/workgroup';
import routeList from './modules/routeList';
import routeEdit from './modules/routeEdit';
import stations from './modules/stations';
import planner from './modules/planner';
import flexiride from './modules/flexiride';
import dispatch from './modules/dispatch';
import riders from './modules/riders';
import notifications from './modules/notifications';
import routeSchedules from './modules/routeSchedules';
import routePersonnel from './modules/routePersonnel';
import routeShifts from './modules/routeShifts';
import routesMap from './modules/routesMap';
import otpDashboard from './modules/otpDashboard';
import ridershipDashboard from './modules/ridershipDashboard';
import onDemandDashboard from './modules/onDemandDashboard';
import userPreferences from './modules/userPreferences';
import myTrips from './modules/myTrips';
import blocks from './modules/blocks';
import assignments from './modules/assignments';
import stops from './modules/stops';
import shuttleRiders from './modules/shuttleRiders';
import ssePlugin from './plugins/ssePlugin';
import rumPlugin from './plugins/rumPlugin';

import registrationService from '../services/registration';

Vue.use(Vuex);

function readMainMenuCollapsed() {
  try {
    return localStorage.getItem('flexigoMainMenuCollapsed') === 'true';
  } catch (e) {
    return false;
  }
}

function readCachedCampuses() {
  try {
    const raw = localStorage.getItem('campusLocation');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    localStorage.removeItem('campusLocation');
    return [];
  }
}

export default new Vuex.Store({
  state: {
    shell: 'blank',
    userDetails: [],
    campuses: readCachedCampuses(),
    // Full campus list (allCampuses=true); the default list excludes primary campuses (VSER-14154)
    allCampuses: [],
    currentRouterComponent: null,
    currentComponent: null,
    selectedStep: 0,
    personnel: [], // Used by onboarding module (addEmployee.vue, onboardingMap.vue)
    isBackgroundWhite: false,
    companyName: null,
    allWorkgroups: null,
    selectedWorkgroupId: null,
    companySettings: null,
    serverTimezone: null,
    mainMenu: [],
    selectedPersonnel: null,
    isSideMenuVisible: false,
    // Desktop-only: collapses the main menu down to an icon rail (labels shown on hover)
    isMainMenuCollapsed: readMainMenuCollapsed(),
    isLoading: false,
    isNavigating: false,
    slidingPanelViewMode: 'VIEW',
    formChanged: false,
    appType: 'corporate', // added for flexicar vehicle page
  },
  mutations: {
    RESET(state) {
      state = {};
      localStorage.clear();
      sessionStorage.clear();
    },
    SET_LAYOUT(state, payload) {
      state.shell = payload;
    },
    SET_USER(state, payload) {
      state.user = payload.data;
      localStorage.setItem('_token', state.user.token);
    },
    SET_SIDEBAR_STATUS(state, status) {
      state.isSidebarOpen = status;
    },
    SET_CAMPUSES(state, payload) {
      state.campuses = payload;
      localStorage.setItem('campusLocation', JSON.stringify(payload));
    },
    SET_ALL_CAMPUSES(state, payload) {
      state.allCampuses = payload;
    },
    SET_CURRENT_ROUTER_COMPONENT(state, payload) {
      state.currentRouterComponent = payload;
    },
    SET_CURRENT_COMPONENT(state, payload) {
      state.currentComponent = payload;
    },
    SET_SELECTED_STEP(state, payload) {
      state.selectedStep = payload;
    },
    SET_PERSONNEL_DATA(state, payload) {
      state.personnel = payload;
    },
    SET_IS_BACKGROUND_WHITE(state, payload) {
      state.isBackgroundWhite = payload;
    },
    SET_COMPANY_NAME(state, payload) {
      state.companyName = payload;
    },
    SET_WORKGROUPS(state, payload) {
      state.allWorkgroups = payload;
      localStorage.setItem('workgroups', JSON.stringify(payload));
    },
    SET_SELECTED_WORKGROUP_ID(state, payload) {
      state.selectedWorkgroupId = payload;
    },
    SET_COMPANY_SETTINGS(state, payload) {
      state.companySettings = payload;
    },
    SET_SERVER_TIMEZONE(state, payload) {
      state.serverTimezone = payload;
    },
    SET_USER_DETAILS(state, payload) {
      state.userDetails = payload;
    },
    SET_COMPANY_ID(state, payload) {
      state.companySettings.companyId = payload;
    },
    SET_MAIN_MENU(state, items) {
      state.mainMenu = items;
    },
    SET_SELECTED_PERSONNEL(state, payload) {
      state.selectedPersonnel = payload;
    },
    SET_IS_SIDE_MENU_VISIBLE(state, payload) {
      state.isSideMenuVisible = payload;
    },
    SET_IS_MAIN_MENU_COLLAPSED(state, payload) {
      state.isMainMenuCollapsed = payload;
      try {
        localStorage.setItem('flexigoMainMenuCollapsed', payload ? 'true' : 'false');
      } catch (e) {
        // storage unavailable (private mode) - the collapse state just won't survive a reload
      }
    },
    SET_IS_LOADING(state, payload) {
      state.isLoading = payload;
    },
    SET_IS_NAVIGATING(state, payload) {
      state.isNavigating = payload;
    },
    SET_SLIDING_PANEL_VIEW_MODE(state, payload) {
      state.slidingPanelViewMode = payload;
    },
    SET_FORM_CHANGED(state, payload) {
      state.formChanged = payload;
    },
  },
  actions: {
    async SET_MAIN_MENU(store, options = { lang: 'en', isRider: false }) {
      const r = await auth.getMainMenu(options.lang, options.isRider);
      store.commit('SET_MAIN_MENU', r);
    },
    toggleSideMenu(store) {
      store.commit('SET_IS_SIDE_MENU_VISIBLE', !store.state.isSideMenuVisible);
    },
    SET_IS_SIDE_MENU_VISIBLE(store, payload) {
      store.commit('SET_IS_SIDE_MENU_VISIBLE', payload);
    },
    toggleMainMenuCollapsed(store) {
      store.commit('SET_IS_MAIN_MENU_COLLAPSED', !store.state.isMainMenuCollapsed);
    },
    async SET_COMPANY_SETTINGS(store, value) {
      const response = await globalService.getCompanySettings();
      store.commit('SET_COMPANY_SETTINGS', response);
    },
    async SET_SERVER_TIMEZONE(store) {
      const response = await globalService.getServerTimezone();
      store.commit('SET_SERVER_TIMEZONE', response);
    },
    async SET_USER_DETAILS(store, value) {
      const response = await registrationService.verify();

      store.commit('SET_USER_DETAILS', response.response);
    },
    async SET_WORKGROUPS(store, value) {
      const response = await workgroupService.getAll();
      const templates = objectifyByKey(response.templates, 'id');
      const combineWorkgroups = response.instances.map((el) => ({ template: templates[el.templateId], instance: el }));
      combineWorkgroups.sort((x, y) => (x.instance.isDefault > y.instance.isDefault ? -1 : 0));
      store.commit('SET_WORKGROUPS', combineWorkgroups);
    },
    async SET_CAMPUSES(store, value) {
      const response = await companies.getCampuses(store.state.userDetails.accountType);
      store.commit('SET_CAMPUSES', response);
    },
    async SET_ALL_CAMPUSES(store) {
      if (store.state.allCampuses.length) return;
      const response = await companies.getCampuses(store.state.userDetails.accountType, null, null, true);
      store.commit('SET_ALL_CAMPUSES', response || []);
    },
  },
  getters: {
    shell(state) {
      return state.shell;
    },
    application(state) {
      if (!state.user) return null;
      return state.user.applicationName;
    },
    user(state) {
      return state.user;
    },
    entryPage(state) {
      return state?.user?.entryPage;
    },
    hasPermission(state) {
      if (!state?.userDetails?.permissions) return () => false;
      const permissionList = state.userDetails.permissions;
      return (permission) => Boolean(permissionList.find((el) => el === permission));
    },
    // Combines the near-demand feature flags with the suspension-endpoint permission.
    // Gates both the suspendedUntil/suspendedBy grid columns and the suspend
    // field on the edit panel, so neither can show without the other being true.
    isNearDemandSuspensionVisible(state, getters) {
      const { nearDemandNoShowRuleEnabled, nearDemandCancellationRuleEnabled } = state.companySettings || {};
      const hasNearDemandRule = !!(nearDemandNoShowRuleEnabled || nearDemandCancellationRuleEnabled);

      return hasNearDemandRule && getters.hasPermission('portalPersonnel:createOrUpdate');
    },
    hasRole(state) {
      if (!state?.userDetails?.permissions) return false;
      return (role) => state.userDetails.scopes.includes(role);
    },
    campuses(state) {
      return (id) => {
        if (!id) return state.campuses;
        return state.campuses.find((el) => id === el.id);
      };
    },
    allWorkgroups(state) {
      return state.allWorkgroups;
    },

    userDetails(state) {
      const isRider = !state.userDetails.accountType;
      const userDetails = {
        isRider,
        fullName: isRider ? state.userDetails?.personnel?.fullName : state.userDetails?.cn,
        email: isRider ? state.userDetails?.personnel?.email : state.userDetails?.uid,
        personnelDetails: isRider ? state.userDetails?.personnel : undefined,
        disabilityPreferenceList: isRider ? state.userDetails?.disabilityPreferenceList : undefined,
      };
      return userDetails;
    },
    slidingPanelViewMode(state) {
      return state.slidingPanelViewMode;
    },
    isRider(state) {
      return state.userDetails?.personnel?.isActive && !state.userDetails?.accountType;
    },
    isAdmin(state) {
      return state.userDetails?.accountType;
    },
  },
  modules: {
    settings,
    settingsTabs,
    workgroups,
    routeList,
    routeEdit,
    stations,
    planner,
    flexiride,
    dispatch,
    riders,
    notifications,
    routeSchedules,
    routePersonnel,
    routeShifts,
    routesMap,
    otpDashboard,
    ridershipDashboard,
    onDemandDashboard,
    userPreferences,
    myTrips,
    blocks,
    assignments,
    stops,
    shuttleRiders,
  },
  plugins: [ssePlugin, rumPlugin],
});
/* eslint-enable no-param-reassign */
/* eslint-enable no-unused-vars */
